/**
 * create-board/CloneFromBoardPicker — the "Clone from existing board…"
 * collapsible section of CreateBoardDialog. Extracted VERBATIM from the former
 * monolith; only renders when the parent supplies `loadTemplateCandidates`.
 */

import { Copy } from 'lucide-react'
import type { Board } from '../../types'

export interface CloneFromBoardPickerProps {
  templatePickerOpen: boolean
  setTemplatePickerOpen: (updater: (o: boolean) => boolean) => void
  templates: Board[] | null
  templatesLoading: boolean
  templateFilter: string
  setTemplateFilter: (value: string) => void
  cloneFromBoardId: string
  setCloneFromBoardId: (id: string) => void
  cloneConfig: boolean
  setCloneConfig: (value: boolean) => void
  selectedTemplate: Board | undefined
}

export function CloneFromBoardPicker({
  templatePickerOpen,
  setTemplatePickerOpen,
  templates,
  templatesLoading,
  templateFilter,
  setTemplateFilter,
  cloneFromBoardId,
  setCloneFromBoardId,
  cloneConfig,
  setCloneConfig,
  selectedTemplate,
}: CloneFromBoardPickerProps) {
  return (
    <div className="rounded-md border border-neutral-200 p-2 dark:border-neutral-800">
      <button
        type="button"
        onClick={() => setTemplatePickerOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <span className="inline-flex items-center gap-2 text-xs font-medium text-neutral-700 dark:text-neutral-200">
          <Copy className="h-3 w-3 text-neutral-400" />
          Clone from existing board…
        </span>
        <span className="text-[10px] text-neutral-400">
          {selectedTemplate
            ? `Selected: ${selectedTemplate.name}`
            : (templatePickerOpen ? 'Pick one below' : 'Optional')}
        </span>
      </button>
      {templatePickerOpen && (
        <div className="mt-2 space-y-1.5">
          <input
            type="search"
            value={templateFilter}
            onChange={(e) => setTemplateFilter(e.target.value)}
            placeholder="Search boards…"
            className="w-full rounded border border-neutral-200 bg-neutral-50 px-2 py-1 text-xs text-neutral-700 placeholder-neutral-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30 dark:border-zinc-800 dark:bg-zinc-900 dark:text-neutral-200"
          />
          <div className="max-h-44 overflow-y-auto rounded border border-neutral-100 dark:border-neutral-800">
            {templatesLoading && (
              <div className="px-2 py-3 text-center text-[11px] text-neutral-400">
                Loading…
              </div>
            )}
            {!templatesLoading && (templates ?? []).length === 0 && (
              <div className="px-2 py-3 text-center text-[11px] text-neutral-400">
                No boards yet.
              </div>
            )}
            {/* "None" option to back out of cloning */}
            {!templatesLoading && (templates ?? []).length > 0 && (
              <button
                type="button"
                onClick={() => setCloneFromBoardId('')}
                className={[
                  'flex w-full items-center justify-between px-2 py-1.5 text-left text-xs',
                  !cloneFromBoardId
                    ? 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300'
                    : 'hover:bg-neutral-100 dark:hover:bg-neutral-900',
                ].join(' ')}
              >
                <span>— None (use canonical template above)</span>
              </button>
            )}
            {(templates ?? [])
              .filter((b) => !b.archived)
              .filter((b) => {
                const q = templateFilter.trim().toLowerCase()
                if (!q) return true
                return b.name.toLowerCase().includes(q) || b.kind.toLowerCase().includes(q)
              })
              .slice(0, 30)
              .map((b) => {
                const active = cloneFromBoardId === b.id
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setCloneFromBoardId(b.id)}
                    className={[
                      'flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left text-xs',
                      active
                        ? 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300'
                        : 'hover:bg-neutral-100 dark:hover:bg-neutral-900',
                    ].join(' ')}
                  >
                    <span className="truncate">{b.name}</span>
                    <span className="shrink-0 text-[10px] uppercase tracking-wide text-neutral-400">
                      {b.kind.replace('_', ' ')}
                    </span>
                  </button>
                )
              })}
          </div>
          {selectedTemplate && (
            <label className="flex items-center gap-2 text-[11px] text-neutral-600 dark:text-neutral-300">
              <input
                type="checkbox"
                checked={cloneConfig}
                onChange={(e) => setCloneConfig(e.target.checked)}
                className="h-3 w-3 accent-blue-600"
              />
              Also copy theme + background
            </label>
          )}
        </div>
      )}
    </div>
  )
}
